import { Injectable } from "@angular/core";
import {
    AbstractListStateService, ITaskListFilterOptions
} from "@shopliftr/common-ng";
import { Task } from "@shopliftr/common-js/admin";
import {
    BehaviorSubject, Observable
} from "rxjs";

/**
 * List state for the task list.
 *
 * Inherits paging, filter options, row states and caching from
 * {@link AbstractListStateService}. It adds the task being worked in the current
 * session and the id of the "Deal Entry Task Open" New Relic interaction handed from
 * task workflow to the deal cache resolver. Row expansion and the flyer fetched for an
 * expanded row are held on {@link TaskListComponent}, because they are view state that
 * dies with the view.
 *
 * **Service Pattern:** List State Service
 */
@Injectable({
    providedIn: "root"
})
export class TaskListStateService extends AbstractListStateService<Task, ITaskListFilterOptions> {

    private readonly _selectedTask$ = new BehaviorSubject<Task>(undefined);

    private _taskOpenInteractionId: string | undefined;

    get selectedTask$(): Observable<Task> {

        return this._selectedTask$.asObservable();
    }

    get selectedTask(): Task {

        return this._selectedTask$.value;
    }

    /**
     * Sets the task being worked in the current session.
     */
    updateSelectedTask(task: Task): void {

        this._selectedTask$.next(task);
    }

    /**
     * Clears the selected task.
     */
    clearSelectedTask(): void {

        this._selectedTask$.next(undefined);
    }

    /**
     * Stores the id of the "Deal Entry Task Open" New Relic interaction until
     * {@link takeTaskOpenInteractionId} collects it.
     *
     * Hand-off: `TaskWorkflowService.workTask` stores the id and `dealCacheResolver`
     * takes it and ends the interaction. It is not cleared by {@link clearSelectedTask}
     * or `reset()`, because it must survive the navigation between the two.
     *
     * @param id - the interaction id returned by the logger's `startInteraction`
     */
    startTaskOpenInteraction(id: string): void {

        this._taskOpenInteractionId = id;
    }

    /**
     * Returns the stored "Deal Entry Task Open" interaction id and clears it, so only
     * the first caller ends the interaction.
     *
     * @returns the interaction id, or `undefined` when none is open
     */
    takeTaskOpenInteractionId(): string | undefined {

        const id = this._taskOpenInteractionId;

        this._taskOpenInteractionId = undefined;

        return id;
    }

    protected _getRowId(row: Task): string {

        return row.id;
    }
}
